/**
 * OptiPrime Intelligent Feedback Manager - Admin Panel Component
 * Displays AI-processed feedback with priority, code locations, and admin actions
 * Created: 2025-01-30
 * Updated: 2025-01-30 - Enhanced priority view with comprehensive details
 */

import React, { useState, useEffect, useRef } from 'react';
import './FeedbackManager.css';

const FeedbackManager = () => {
    const [feedback, setFeedback] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
        category: '',
        status: '', // Default to 'all' (empty string = all status)
        limit: 100, // Show all items at once (increased from 10)
        offset: 0   // Keep offset for compatibility
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        totalPages: 0,
        itemsPerPage: 10
    });
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [replyTarget, setReplyTarget] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [individualFeedback, setIndividualFeedback] = useState({});
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [promptResult, setPromptResult] = useState(null);
    const [pendingEdits, setPendingEdits] = useState({});
    const [rejectModalFeedback, setRejectModalFeedback] = useState(null);
    const [rejectReasonDraft, setRejectReasonDraft] = useState('');
    const [summaryData, setSummaryData] = useState(null); // Track pending item edits
    /** After the first fetch, never swap the whole page for the spinner (avoids scroll jump on filter change). */
    const feedbackFetchStartedRef = useRef(false);
    const [listRefreshing, setListRefreshing] = useState(false);

    // Detect environment and set API base URL
    const getApiBaseUrl = () => {
        const hostname = window.location.hostname;
        if (hostname === 'admin.optiprime.ai') {
            return 'https://admin.optiprime.ai'; // Production API (OptiQuery backend via proxy)
        }
        // Spark development environments (Ethernet and WiFi)
        if (hostname === '10.0.0.169' || hostname === '10.0.0.177') {
            return `http://${hostname}:5002`; // Spark dev backend
        }
        return 'http://5.78.121.92:5002'; // Hetzner development API
    };

    // Fetch feedback data
    useEffect(() => {
        fetchFeedback();
        fetchStats();
        // Fetch summary data when on "All Status" view
        if (!filter.status || filter.status === '' || filter.status === 'all') {
            fetchSummaryData();
        }
    }, [filter]);

    const fetchFeedback = async () => {
        const firstFetch = !feedbackFetchStartedRef.current;
        try {
            if (firstFetch) {
                setLoading(true);
            } else {
                setListRefreshing(true);
            }
            const apiUrl = getApiBaseUrl();
            const queryParams = new URLSearchParams(filter).toString();
            const fullUrl = `${apiUrl}/api/feedback/admin/priority?${queryParams}`;
            
            console.log('[DEBUG] Fetching feedback from:', fullUrl);
            const response = await fetch(fullUrl);
            console.log('[DEBUG] Response status:', response.status);
            
            const data = await response.json();
            console.log('[DEBUG] Response data:', data);
            
            if (data.success) {
                console.log('[DEBUG] Setting feedback:', data.feedback.length, 'items');
                setFeedback(data.feedback);
                
                // Update pagination info if provided by API
                if (data.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        totalItems: data.pagination.total,
                        totalPages: Math.ceil(data.pagination.total / prev.itemsPerPage),
                        currentPage: Math.floor(filter.offset / prev.itemsPerPage) + 1
                    }));
                }
            } else {
                console.error('Failed to fetch feedback:', data.error);
            }
        } catch (error) {
            console.error('Error fetching feedback:', error);
        } finally {
            setLoading(false);
            setListRefreshing(false);
            feedbackFetchStartedRef.current = true;
        }
    };

    const fetchStats = async () => {
        try {
            const queryParams = new URLSearchParams();
            if (filter.category) {
                queryParams.append('category', filter.category);
            }
            // Pass the current status filter to stats API (only if not 'all')
            if (filter.status && filter.status !== '') {
                queryParams.append('status', filter.status);
            }
            
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/admin/stats?${queryParams.toString()}`);
            const data = await response.json();
            
            if (data.success) {
                setStats(data.stats.overall);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchSummaryData = async () => {
        try {
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/admin/summary`);
            if (response.ok) {
                const result = await response.json();
                setSummaryData(result.summary);
            }
        } catch (error) {
            console.error('Error fetching summary data:', error);
        }
    };

    const fetchIndividualFeedback = async (groupId) => {
        if (!groupId || individualFeedback[groupId]) return; // Already fetched or no group
        
        try {
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/group/${groupId}/individual`);
            const data = await response.json();
            
            if (data.success) {
                setIndividualFeedback(prev => ({
                    ...prev,
                    [groupId]: data.individual_feedback
                }));
            }
        } catch (error) {
            console.error('Error fetching individual feedback:', error);
        }
    };

    const handleExpandGroup = (item) => {
        const groupId = item.similarity_group_id;
        const isExpanded = expandedGroups[item.id];
        
        setExpandedGroups(prev => ({
            ...prev,
            [item.id]: !isExpanded
        }));
        
        // Fetch individual feedback if expanding and it's a group
        if (!isExpanded && groupId) {
            fetchIndividualFeedback(groupId);
        }
    };

    const handleAction = async (feedbackId, action, notes = '') => {
        try {
            // Add confirmation dialog for delete_forever action
            if (action === 'delete_forever') {
                const confirmed = window.confirm('Are you sure you want to delete this feedback forever? This will hide it from the dashboard permanently (but keep it in the database for audit purposes).');
                if (!confirmed) {
                    return; // User cancelled, don't proceed
                }
            }

            const response = await fetch(`${getApiBaseUrl()}/api/feedback/admin/action`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedback_id: feedbackId,
                    action_type: action,
                    admin_user_id: 'admin', // TODO: Get from user context
                    admin_notes: notes
                })
            });

            const data = await response.json();
            if (data.success) {
                fetchFeedback(); // Refresh the list
                fetchStats(); // Refresh the stats to reflect changes
                
                // Show appropriate success message
                if (action === 'delete_forever') {
                    alert('Feedback has been permanently hidden from the dashboard.');
                } else if (action === 'undo') {
                    alert('Feedback has been restored to pending status.');
                }
            } else {
                alert('Action failed: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error performing action:', error);
            alert('Error performing action. Please try again.');
        }
    };

    const handleReply = (item) => {
        setReplyTarget(item);
        setShowReplyModal(true);
    };

    const sendReply = async (subject, message) => {
        try {
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedback_id: replyTarget.id,
                    to_email: replyTarget.user_email,
                    subject: subject,
                    message: message,
                    admin_email: 'contact@optiprime.ai'
                })
            });

            const data = await response.json();
            if (data.success) {
                alert('Reply sent successfully!');
                setShowReplyModal(false);
                setReplyTarget(null);
            } else {
                alert('Error sending reply: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            alert('Error sending reply. Please try again.');
        }
    };

    const handleProcessPrompt = async (feedbackId) => {
        try {
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/process-prompt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    feedback_id: feedbackId,
                    admin_user_id: 'admin'
                })
            });

            if (response.ok) {
                const result = await response.json();
                setPromptResult(result);
                setShowPromptModal(true);
            } else {
                const error = await response.text();
                alert(`Error generating prompt: ${error}`);
            }
        } catch (error) {
            console.error('Error processing prompt:', error);
            alert('Error processing prompt. Please try again.');
        }
    };

    // Pagination functions
    const handlePageChange = (newPage) => {
        const newOffset = (newPage - 1) * pagination.itemsPerPage;
        setFilter(prev => ({
            ...prev,
            offset: newOffset
        }));
        setPagination(prev => ({
            ...prev,
            currentPage: newPage
        }));
    };

    const handlePreviousPage = () => {
        if (pagination.currentPage > 1) {
            handlePageChange(pagination.currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (pagination.currentPage < pagination.totalPages) {
            handlePageChange(pagination.currentPage + 1);
        }
    };

    // Handle category changes for pending items - Support multiple categories
    const handleCategoryChange = (feedbackId, newCategory) => {
        setPendingEdits(prev => {
            const currentEdits = prev[feedbackId] || {};
            const originalItem = feedback.find(f => f.id === feedbackId);
            const currentCategories = currentEdits.categories || [originalItem?.category].filter(Boolean);
            
            // Toggle selection - add if not present, remove if present
            let updatedCategories;
            if (currentCategories.includes(newCategory)) {
                // Remove category if already selected
                updatedCategories = currentCategories.filter(cat => cat !== newCategory);
            } else {
                // Add category if not selected
                updatedCategories = [...currentCategories, newCategory];
            }
            
            return {
                ...prev,
                [feedbackId]: {
                    ...currentEdits,
                    categories: updatedCategories,
                    // Keep the primary category as the first one for backward compatibility
                    category: updatedCategories[0] || originalItem?.category
                }
            };
        });
        
        console.log(`Categories updated for feedback ${feedbackId}:`, newCategory);
    };

    // Get selected categories for display
    const getSelectedCategories = (item) => {
        const edits = pendingEdits[item.id];
        if (edits && edits.categories) {
            return edits.categories;
        }
        return [item.category].filter(Boolean);
    };

    // Check if a category is selected
    const isCategorySelected = (feedbackId, category) => {
        const edits = pendingEdits[feedbackId];
        if (edits && edits.categories) {
            return edits.categories.includes(category);
        }
        const originalItem = feedback.find(f => f.id === feedbackId);
        return originalItem?.category === category;
    };

    // Handle code location changes for pending items
    const handleLocationChange = (feedbackId, field, value) => {
        setPendingEdits(prev => ({
            ...prev,
            [feedbackId]: {
                ...prev[feedbackId],
                [field]: value
            }
        }));
    };

    // Process pending item → processed (categories, code location, notes)
    const handlePendingAction = async (feedbackId) => {
        try {
            const edits = pendingEdits[feedbackId] || {};
            const originalItem = feedback.find(f => f.id === feedbackId);
            
            // Prepare admin edits with categories support
            // Ensure we always include the original category even if no additional categories selected
            const selectedCategories = edits.categories || [originalItem?.category].filter(Boolean);
            const adminEdits = {
                ...edits,
                // Send primary category for backend compatibility
                category: selectedCategories[0] || originalItem?.category,
                // Also send all categories for future enhancement
                all_categories: selectedCategories
            };
            
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/admin/action`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedback_id: feedbackId,
                    action_type: 'promote_to_processed',
                    admin_user_id: 'admin',
                    admin_edits: adminEdits,
                    admin_notes: `Admin reviewed and processed with ${adminEdits.all_categories.length} categories`
                })
            });

            const data = await response.json();
            if (data.success) {
                // Clear edits for this item
                setPendingEdits(prev => {
                    const newEdits = { ...prev };
                    delete newEdits[feedbackId];
                    return newEdits;
                });
                
                // Refresh the feedback list
                fetchFeedback();
                fetchStats();
                alert(`Item processed successfully with ${adminEdits.all_categories.length} categories!`);
            } else {
                console.error('Failed to process pending action:', data.error);
                alert('Failed to process action: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error processing pending action:', error);
            alert('Error processing action. Please try again.');
        }
    };

    const openRejectModal = (item) => {
        setRejectModalFeedback(item);
        setRejectReasonDraft('');
    };

    const submitReject = async () => {
        const reason = rejectReasonDraft.trim();
        if (reason.length < 8) {
            alert('Please enter a rejection reason for the submitter (at least 8 characters).');
            return;
        }
        const item = rejectModalFeedback;
        if (!item) return;
        try {
            const response = await fetch(`${getApiBaseUrl()}/api/feedback/admin/action`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedback_id: item.id,
                    action_type: 'reject',
                    admin_user_id: 'admin',
                    action_details: { rejection_reason: reason },
                }),
            });
            const data = await response.json();
            if (data.success) {
                setRejectModalFeedback(null);
                setRejectReasonDraft('');
                if (item.processing_status === 'pending') {
                    setPendingEdits((prev) => {
                        const next = { ...prev };
                        delete next[item.id];
                        return next;
                    });
                }
                fetchFeedback();
                fetchStats();
                alert(
                    data.already_rejected
                        ? 'This item was already rejected.'
                        : 'Feedback rejected. The submitter will receive an email with your reason and their current Opti balance.'
                );
            } else {
                alert('Rejection failed: ' + (data.error || 'Unknown error'));
            }
        } catch (e) {
            console.error('Reject error:', e);
            alert('Error rejecting feedback. Please try again.');
        }
    };

    const handlePriorityAdjust = async (feedbackId, adjustment) => {
        try {
            // Find the current item
            const currentItem = feedback.find(item => item.id === feedbackId);
            if (!currentItem) return;

            const newPriority = Math.round((currentItem.priority_score + adjustment) * 100) / 100; // Round to 2 decimal places
            
            // Prevent negative priorities
            if (newPriority < 0) {
                alert('Priority cannot be negative');
                return;
            }

            const response = await fetch(`${getApiBaseUrl()}/api/feedback/admin/action`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedback_id: feedbackId,
                    action_type: 'modify_priority',
                    admin_user_id: 'admin',
                    action_details: {
                        new_priority: newPriority
                    }
                })
            });

            const data = await response.json();
            if (data.success) {
                // Refresh the feedback list to show new order (auto-reordering)
                fetchFeedback();
                fetchStats(); // Refresh stats after priority changes
            } else {
                console.error('Failed to update priority:', data.error);
                alert('Failed to update priority: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error updating priority:', error);
            alert('Error updating priority. Please try again.');
        }
    };

    const getCategoryIcon = (category) => {
        const icons = {
            bug: '🐛',
            ui: '🎨',
            ux: '👤',
            functionality: '⚙️',
            performance: '⚡',
            security: '🔒',
            feature: '✨',
            property_management: '🏢',
            ai_assistant: '🤖',
            legal_platform: '⚖️',
            user_management: '👥',
            ui_design: '🎨',
            mobile_experience: '📱',
            bug_reports: '🐛',
            feature_requests: '✨'
        };
        return icons[category] || '💭';
    };

    const safePriority = (item) => {
        const n = Number(item?.priority_score);
        return Number.isFinite(n) ? n : 0;
    };

    const getCategoryColor = (category) => {
        const colors = {
            bug: '#ff4757',
            ui: '#3742fa',
            ux: '#2ed573',
            functionality: '#ffa502',
            performance: '#ff6b81',
            security: '#ff3838',
            feature: '#5352ed',
            property_management: '#ffa502',
            ai_assistant: '#5352ed',
            legal_platform: '#2f3542',
            user_management: '#3742fa',
            ui_design: '#3742fa',
            mobile_experience: '#2ed573',
            bug_reports: '#ff4757',
            feature_requests: '#5352ed'
        };
        return colors[category] || '#747d8c';
    };

    const formatPageContext = (pageContext) => {
        if (!pageContext) return { text: 'Unknown Page', url: null };
        try {
            const context = typeof pageContext === 'string' ? JSON.parse(pageContext) : pageContext;
            // Show the full URL if available, otherwise fallback to section
            if (context.url) {
                return { text: context.url, url: context.url };
            } else if (context.section) {
                return { text: context.section, url: null };
            }
            return { text: 'Unknown Page', url: null };
        } catch (e) {
            console.log('Error parsing page context:', e, pageContext);
            return { text: 'Unknown Page', url: null };
        }
    };

    const getPriorityLevel = (score) => {
        if (score >= 8) return { level: 'Critical', color: '#ff4757', icon: '🔥' };
        if (score >= 6) return { level: 'High', color: '#ffa502', icon: '⚠️' };
        if (score >= 4) return { level: 'Medium', color: '#3742fa', icon: '📋' };
        return { level: 'Low', color: '#747d8c', icon: '📝' };
    };

    const getCredibilityBadge = (reputation, totalFeedback) => {
        if (reputation >= 2.0) return { label: 'Expert', color: '#2ed573' };
        if (reputation >= 1.0) return { label: 'Experienced', color: '#3742fa' };
        if (reputation >= 0.5) return { label: 'Regular', color: '#ffa502' };
        return { label: 'New', color: '#747d8c' };
    };

    const getCategoryDisplayName = (category) => {
        if (!category) return 'All Categories';
        const names = {
            'property_management': 'Property Management',
            'ai_assistant': 'AI Assistant',
            'legal_platform': 'Legal Platform',
            'user_management': 'User Management',
            'ui_design': 'UI/UX Design',
            'performance': 'Performance',
            'mobile_experience': 'Mobile Experience',
            'bug_reports': 'Bug Reports',
            'feature_requests': 'Feature Requests',
            // Legacy categories for backward compatibility
            'bug': 'Bug Reports',
            'functionality': 'Property Management',
            'feature_request': 'Feature Requests',
            'legal_compliance': 'Legal Platform'
        };
        return names[category] || category.replace('_', ' ').toUpperCase();
    };

    // Dynamic stats title based on current filters
    const getStatsTitle = () => {
        const hasCategory = filter.category && filter.category !== '';
        const hasStatus = filter.status && filter.status !== '' && filter.status !== 'all';
        
        if (hasCategory && hasStatus) {
            const statusName = filter.status.charAt(0).toUpperCase() + filter.status.slice(1);
            const categoryName = getCategoryDisplayName(filter.category);
            return `${statusName} ${categoryName}`;
        } else if (hasStatus) {
            return filter.status.charAt(0).toUpperCase() + filter.status.slice(1) + ' Feedback';
        } else if (hasCategory) {
            return getCategoryDisplayName(filter.category);
        } else {
            return 'All Categories';
        }
    };

    // Dynamic stats subtitle based on current filters
    const getStatsSubtitle = () => {
        const hasCategory = filter.category && filter.category !== '';
        const hasStatus = filter.status && filter.status !== '' && filter.status !== 'all';
        
        if (hasCategory && hasStatus) {
            return `${filter.status} items in ${getCategoryDisplayName(filter.category).toLowerCase()} category only`;
        } else if (hasStatus) {
            return `All ${filter.status} items across all categories`;
        } else if (hasCategory) {
            return `All statuses in ${getCategoryDisplayName(filter.category).toLowerCase()} category`;
        } else {
            return 'All categories combined';
        }
    };

    if (loading) {
        return (
            <div className="feedback-loading">
                <div className="loading-spinner"></div>
                <p>Loading intelligent feedback analysis...</p>
            </div>
        );
    }

    // Compute tactical status banner from live stats
    const tacticalLevel = (() => {
        if (!stats) return 'unknown';
        const pending = Number(stats.total_pending || 0);
        const total = Number(stats.total_feedback || 0);
        if (total === 0) return 'good';
        const pendingRatio = pending / total;
        if (pending >= 30 || pendingRatio >= 0.6) return 'critical';
        if (pending >= 10 || pendingRatio >= 0.3) return 'warning';
        return 'good';
    })();
    const tacticalLabel = {
        good: 'TRIAGE QUEUE HEALTHY',
        warning: 'BACKLOG BUILDING',
        critical: 'BACKLOG CRITICAL',
        unknown: 'AWAITING TELEMETRY',
    }[tacticalLevel];

    const totalFeedback = Number(stats?.total_feedback || 0);
    const totalPending = Number(stats?.total_pending || 0);
    const uniqueUsers = Number(stats?.unique_users || 0);
    const avgPriority = Number(stats?.avg_priority_score || 0);
    const pendingPct = totalFeedback ? (totalPending / totalFeedback) * 100 : 0;
    const priorityPct = Math.min(100, (avgPriority / 10) * 100);
    const priorityLevel = avgPriority >= 7 ? 'critical' : avgPriority >= 4 ? 'warning' : 'good';
    const pendingLevel = pendingPct >= 60 ? 'critical' : pendingPct >= 30 ? 'warning' : 'good';

    return (
        <div className={`feedback-manager opo-tactical feedback-tactical${listRefreshing ? ' feedback-manager--list-refreshing' : ''}`}>
            {/* ─── Tactical hero + KPI strip ─────────────────────────── */}
            {stats && (
                <div className="feedback-tactical-top">
                    <div className={`opo-hero opo-hero-${tacticalLevel}`}>
                        <div className="opo-hero-scan" aria-hidden="true" />
                        <div className="opo-hero-left">
                            <div className="opo-hero-eyebrow">FEEDBACK OPERATIONS</div>
                            <div className="opo-hero-title">
                                <span className={`opo-hero-pulse opo-pulse-${tacticalLevel}`} />
                                <h2>{tacticalLabel}</h2>
                            </div>
                            <div className="opo-hero-sub">
                                {totalFeedback} submission{totalFeedback === 1 ? '' : 's'} · {totalPending} pending review · {uniqueUsers} unique contributor{uniqueUsers === 1 ? '' : 's'}
                            </div>
                        </div>
                        <div className="opo-hero-right">
                            <button
                                type="button"
                                className="opo-refresh-btn"
                                onClick={() => { fetchFeedback(); fetchStats(); fetchSummaryData(); }}
                                title="Re-fetch feedback, stats & summary"
                            >
                                <span className="opo-refresh-icon" /> SYNC
                            </button>
                            <div className="opo-hero-timestamp">
                                {new Date().toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div className="opo-kpi-row">
                        <div className="opo-kpi">
                            <div className="opo-kpi-label">TOTAL FEEDBACK</div>
                            <div className="opo-kpi-value opo-mono-value">{totalFeedback.toLocaleString()}</div>
                            <div className="opo-kpi-foot">{getStatsTitle().toLowerCase()}</div>
                        </div>
                        <div className="opo-kpi">
                            <div className="opo-kpi-label">UNIQUE USERS</div>
                            <div className="opo-kpi-value opo-mono-value opo-level-good">{uniqueUsers.toLocaleString()}</div>
                            <div className="opo-kpi-foot">{(uniqueUsers > 0 ? totalFeedback / uniqueUsers : 0).toFixed(1)} avg / user</div>
                        </div>
                        <div className="opo-kpi">
                            <div className="opo-kpi-label">AVG PRIORITY</div>
                            <div className={`opo-kpi-value opo-mono-value opo-level-${priorityLevel}`}>
                                {avgPriority.toFixed(2)}<span className="opo-kpi-sep">/</span>10
                            </div>
                            <div className="opo-bar-track opo-bar-mini">
                                <div className={`opo-bar-fill opo-fill-${priorityLevel}`} style={{ width: `${priorityPct}%` }} />
                            </div>
                            <div className="opo-kpi-foot">priority score across queue</div>
                        </div>
                        <div className="opo-kpi">
                            <div className="opo-kpi-label">PENDING REVIEW</div>
                            <div className={`opo-kpi-value opo-mono-value opo-level-${pendingLevel}`}>
                                {totalPending.toLocaleString()}
                            </div>
                            <div className="opo-bar-track opo-bar-mini">
                                <div className={`opo-bar-fill opo-fill-${pendingLevel}`} style={{ width: `${pendingPct}%` }} />
                            </div>
                            <div className="opo-kpi-foot">{pendingPct.toFixed(0)}% of queue awaiting triage</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="feedback-flow-diagram" aria-labelledby="feedback-flow-heading">
                <h2 id="feedback-flow-heading" className="feedback-flow-diagram__heading">
                    How feedback reaches this page
                </h2>
                <div className="feedback-flow-diagram__track" role="list">
                    <div className="feedback-flow-step" role="listitem">
                        <div className="feedback-flow-step__badge feedback-flow-step__badge--app">1</div>
                        <div className="feedback-flow-step__icon" aria-hidden><i className="fas fa-mobile-alt" /></div>
                        <div className="feedback-flow-step__title">App users</div>
                        <div className="feedback-flow-step__body">
                            <strong>app.optiprime.ai</strong>
                            <span className="feedback-flow-step__muted">Beta Feedback modal</span>
                        </div>
                    </div>

                    <div className="feedback-flow-connector" aria-hidden>
                        <span className="feedback-flow-connector__line" />
                        <span className="feedback-flow-connector__arrow"><i className="fas fa-chevron-right" /></span>
                        <span className="feedback-flow-connector__tag">POST</span>
                    </div>

                    <div className="feedback-flow-step" role="listitem">
                        <div className="feedback-flow-step__badge feedback-flow-step__badge--api">2</div>
                        <div className="feedback-flow-step__icon" aria-hidden><i className="fas fa-paper-plane" /></div>
                        <div className="feedback-flow-step__title">Onboarding API</div>
                        <div className="feedback-flow-step__body">
                            <code className="feedback-flow-step__code">/api/feedback/submit</code>
                            <span className="feedback-flow-step__muted">node-onboarding-dashboard · port 5002</span>
                        </div>
                    </div>

                    <div className="feedback-flow-connector" aria-hidden>
                        <span className="feedback-flow-connector__line" />
                        <span className="feedback-flow-connector__arrow"><i className="fas fa-chevron-right" /></span>
                        <span className="feedback-flow-connector__tag">store</span>
                    </div>

                    <div className="feedback-flow-step" role="listitem">
                        <div className="feedback-flow-step__badge feedback-flow-step__badge--db">3</div>
                        <div className="feedback-flow-step__icon" aria-hidden><i className="fas fa-database" /></div>
                        <div className="feedback-flow-step__title">PostgreSQL</div>
                        <div className="feedback-flow-step__body">
                            <code className="feedback-flow-step__code">optiprime_main.feedback_submissions</code>
                            <span className="feedback-flow-step__muted">one row per submission</span>
                        </div>
                    </div>

                    <div className="feedback-flow-connector" aria-hidden>
                        <span className="feedback-flow-connector__line" />
                        <span className="feedback-flow-connector__arrow"><i className="fas fa-chevron-right" /></span>
                        <span className="feedback-flow-connector__tag">proxy</span>
                    </div>

                    <div className="feedback-flow-step" role="listitem">
                        <div className="feedback-flow-step__badge feedback-flow-step__badge--proxy">4</div>
                        <div className="feedback-flow-step__icon" aria-hidden><i className="fas fa-exchange-alt" /></div>
                        <div className="feedback-flow-step__title">OptiQuery admin</div>
                        <div className="feedback-flow-step__body">
                            <code className="feedback-flow-step__code">/api/feedback/admin/priority</code>
                            <span className="feedback-flow-step__muted">admin.optiprime.ai → proxies to 5002</span>
                        </div>
                    </div>

                    <div className="feedback-flow-connector" aria-hidden>
                        <span className="feedback-flow-connector__line" />
                        <span className="feedback-flow-connector__arrow"><i className="fas fa-chevron-right" /></span>
                        <span className="feedback-flow-connector__tag">GET</span>
                    </div>

                    <div className="feedback-flow-step feedback-flow-step--terminal" role="listitem">
                        <div className="feedback-flow-step__badge feedback-flow-step__badge--here">5</div>
                        <div className="feedback-flow-step__icon" aria-hidden><i className="fas fa-clipboard-list" /></div>
                        <div className="feedback-flow-step__title">This page</div>
                        <div className="feedback-flow-step__body">
                            <strong>Intelligent Feedback System</strong>
                            <span className="feedback-flow-step__muted">list, stats &amp; triage below</span>
                        </div>
                    </div>
                </div>
                <p className="feedback-flow-diagram__footnote">
                    <i className="fas fa-book" aria-hidden /> Deeper architecture: <code>Docs/00_START_HERE.md</code> → Code Location Mapping (OptiQuery + OPO).
                </p>
            </div>

            {/* Stats are now rendered in the tactical hero + KPI strip above. */}

            {/* Summary analytics when "All Status" — list always shown below (previously list was hidden here, so submissions looked missing) */}
            {(!filter.status || filter.status === '' || filter.status === 'all') && summaryData && (
                <div className="summary-dashboard">
                        <>
                            {/* Status Breakdown */}
                            <div className="summary-section">
                                <h3 className="opo-section-heading">▮ STATUS BREAKDOWN</h3>
                                {(() => {
                                    const overall = summaryData.overall || {};
                                    const total = (Number(overall.total_pending) || 0)
                                        + (Number(overall.total_processed) || 0)
                                        + (Number(overall.total_approved) || 0)
                                        + (Number(overall.total_rejected) || 0);
                                    const tiles = [
                                        { key: 'pending', label: 'PENDING', sub: 'Awaiting Review', count: Number(overall.total_pending) || 0, level: 'warning' },
                                        { key: 'processed', label: 'PROCESSED', sub: 'Ready for Decision', count: Number(overall.total_processed) || 0, level: 'good' },
                                        { key: 'approved', label: 'APPROVED', sub: 'Implementation Ready', count: Number(overall.total_approved) || 0, level: 'good' },
                                        { key: 'rejected', label: 'REJECTED', sub: 'Not Implemented', count: Number(overall.total_rejected) || 0, level: 'critical' },
                                    ];
                                    return (
                                        <div className="status-breakdown-grid feedback-status-grid">
                                            {tiles.map(t => {
                                                const pct = total ? (t.count / total) * 100 : 0;
                                                return (
                                                    <div
                                                        key={t.key}
                                                        className={`status-card ${t.key} feedback-status-card`}
                                                        onClick={() => setFilter({ ...filter, status: t.key })}
                                                    >
                                                        <div className="feedback-status-card-head">
                                                            <span className={`opo-pip opo-pip-${t.level}`}><span className="opo-pip-core" /></span>
                                                            <span className="feedback-status-card-label">{t.label}</span>
                                                        </div>
                                                        <div className={`status-number opo-mono-value opo-level-${t.level}`}>{t.count}</div>
                                                        <div className="opo-bar-track opo-bar-mini">
                                                            <div className={`opo-bar-fill opo-fill-${t.level}`} style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <div className="status-sublabel">{t.sub} · {pct.toFixed(0)}%</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Category Analysis */}
                            <div className="summary-section">
                                <h3 className="opo-section-heading">▮ CATEGORY ANALYSIS</h3>
                                <div className="category-analysis-grid">
                                    {(summaryData.categories || []).map((cat, index) => (
                                        <div key={cat.category} className="category-analysis-card" onClick={() => setFilter({...filter, category: cat.category})}>
                                            <div className="category-header">
                                                <span className="category-name">{getCategoryDisplayName(cat.category)}</span>
                                                <span className="category-count">{cat.count} items</span>
                                            </div>
                                            <div className="category-stats">
                                                <div className="category-stat">
                                                    <span className="stat-label">% of Total:</span>
                                                    <span className="stat-value">{(summaryData.overall.total_feedback > 0 ? (cat.count / summaryData.overall.total_feedback) * 100 : 0).toFixed(1)}%</span>
                                                </div>
                                                <div className="category-stat">
                                                    <span className="stat-label">Avg User Rep:</span>
                                                    <span className="stat-value">{cat.avg_user_reputation?.toFixed(2) || '0.00'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Top 5 Pages */}
                            <div className="summary-section">
                                <h3 className="opo-section-heading">▮ TOP 5 PAGES BY VOLUME</h3>
                                <div className="top-pages-list">
                                    {(summaryData.top_pages || []).map((page, index) => (
                                        <div key={index} className="page-feedback-card">
                                            <div className="page-rank">#{index + 1}</div>
                                            <div className="page-details">
                                                <div className="page-url">
                                                    {page.url !== 'Unknown' ? (
                                                        <a href={page.url} target="_blank" rel="noopener noreferrer" className="page-link">
                                                            {page.url}
                                                        </a>
                                                    ) : (
                                                        <span>Unknown Page</span>
                                                    )}
                                                </div>
                                                <div className="page-meta">
                                                    <span>Section: {page.section}</span> • 
                                                    <span>Widget: {page.widget}</span>
                                                </div>
                                            </div>
                                            <div className="page-stats">
                                                <div className="page-stat">
                                                    <span className="page-stat-number">{page.feedback_count}</span>
                                                    <span className="page-stat-label">Feedback</span>
                                                </div>
                                                <div className="page-stat">
                                                    <span className="page-stat-number">{(summaryData.overall.total_feedback > 0 ? (page.feedback_count / summaryData.overall.total_feedback) * 100 : 0).toFixed(1)}%</span>
                                                    <span className="page-stat-label">% of Feedback</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* User Engagement */}
                            <div className="summary-section">
                                <h3 className="opo-section-heading">▮ USER ENGAGEMENT OVERVIEW</h3>
                                <div className="user-engagement-grid">
                                    <div className="engagement-card">
                                        <div className="engagement-number">{summaryData.overall.unique_users}</div>
                                        <div className="engagement-label">Total Users</div>
                                        <div className="engagement-sublabel">Submitted Feedback</div>
                                    </div>
                                    <div className="engagement-card">
                                        <div className="engagement-number">{summaryData.recent_activity.recent_feedback}</div>
                                        <div className="engagement-label">Recent Feedback</div>
                                        <div className="engagement-sublabel">Last 7 Days</div>
                                    </div>
                                    <div className="engagement-card">
                                        <div className="engagement-number">{summaryData.recent_activity.recent_users}</div>
                                        <div className="engagement-label">Active Users</div>
                                        <div className="engagement-sublabel">Last 7 Days</div>
                                    </div>
                                    <div className="engagement-card">
                                        <div className="engagement-number">{(summaryData.overall.unique_users > 0 ? summaryData.overall.total_feedback / summaryData.overall.unique_users : 0).toFixed(1)}</div>
                                        <div className="engagement-label">Avg per User</div>
                                        <div className="engagement-sublabel">Feedback Items</div>
                                    </div>
                                </div>
                            </div>

                            {/* Most Active Users */}
                            <div className="summary-section">
                                <h3 className="opo-section-heading">▮ MOST ACTIVE CONTRIBUTORS</h3>
                                <div className="top-users-list">
                                    {(summaryData.user_stats || []).slice(0, 5).map((user, index) => (
                                        <div key={user.user_email} className="user-contribution-card">
                                            <div className="user-rank">#{index + 1}</div>
                                            <div className="user-details">
                                                <div className="user-email">{user.user_email}</div>
                                            </div>
                                            <div className="user-stats">
                                                <div className="user-stat">
                                                    <span className="user-stat-number">{user.feedback_count}</span>
                                                    <span className="user-stat-label">Feedback</span>
                                                </div>
                                                <div className="user-stat">
                                                    <span className="user-stat-number">{(user.feedback_count * 0.01).toFixed(2)}</span>
                                                    <span className="user-stat-label">OptiLevel</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                </div>
            )}

            {/* Filters — directly above Submissions list */}
            <div className="feedback-filters feedback-tactical-filters">
                <span className="feedback-filters-tag">FILTERS</span>
                <select 
                    value={filter.category} 
                    onChange={(e) => setFilter({...filter, category: e.target.value})}
                    className="filter-select"
                >
                    <option value="">All Categories</option>
                    <option value="property_management">{getCategoryIcon('property_management')} Property Management</option>
                    <option value="ai_assistant">{getCategoryIcon('ai_assistant')} AI Assistant</option>
                    <option value="legal_platform">{getCategoryIcon('legal_platform')} Legal Platform</option>
                    <option value="user_management">{getCategoryIcon('user_management')} User Management</option>
                    <option value="ui_design">{getCategoryIcon('ui_design')} UI Design</option>
                    <option value="performance">{getCategoryIcon('performance')} Performance</option>
                    <option value="mobile_experience">{getCategoryIcon('mobile_experience')} Mobile Experience</option>
                    <option value="bug_reports">{getCategoryIcon('bug_reports')} Bug Reports</option>
                    <option value="feature_requests">{getCategoryIcon('feature_requests')} Feature Requests</option>
                </select>

                <select 
                    value={filter.status} 
                    onChange={(e) => setFilter({...filter, status: e.target.value})}
                    className="filter-select"
                >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="processed">Processed</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                </select>

                <button onClick={fetchFeedback} className="refresh-btn">
                    🔄 Refresh
                </button>
            </div>

            <div className="feedback-submissions-header feedback-tactical-submissions-header">
                <h3 className="opo-section-heading">
                    <span className="opo-pip opo-pip-good"><span className="opo-pip-core" /></span>
                    SUBMISSIONS
                    <span className="feedback-submissions-count opo-mono-value">{feedback.length}</span>
                </h3>
                <span className="feedback-submissions-meta">
                    Showing {feedback.length} of {pagination.totalItems ?? feedback.length} after grouping rules.
                </span>
            </div>

                <div className="priority-feedback-list">
                    {feedback.length === 0 ? (
                        <div className="no-feedback">
                            <span className="icon">📭</span>
                            <h3>No feedback found</h3>
                            <p>No feedback matches your current filters</p>
                        </div>
                    ) : (
                        feedback.map((item, index) => {
                        const credibility = getCredibilityBadge(item.user_reputation, item.user_total_feedback);
                        
                        return (
                            <div key={item.id} className={`priority-feedback-card ${item.processing_status === 'pending' ? 'pending-status' : 'non-pending-status'}`}>
                                {/* Compact Feedback Header - No User Info */}
                                <div className="compact-feedback-header">
                                    {/* Left: Priority Number - Large */}
                                    <div className="priority-number-large">#{index + 1}</div>
                                    
                                    {/* Center: Category & File Location */}
                                    <div className="header-center-info">
                                        {/* Category Row - Space for Multiple Categories */}
                                        <div className="category-row-expandable">
                                            {getSelectedCategories(item).map((category, catIndex) => (
                                                <span 
                                                    key={`${item.id}-${category}-${catIndex}`}
                                                    className="category-badge"
                                                    style={{ backgroundColor: getCategoryColor(category) }}
                                                >
                                                    {getCategoryIcon(category)} {category.toUpperCase()}
                                                </span>
                                            ))}
                                            {/* Space reserved for additional categories when edited */}
                                            <div className="additional-categories-space">
                                                {/* This space is now used by the mapped categories above */}
                                            </div>
                                        </div>
                                        
                                        {/* File Location Row - Moved up from below */}
                                        <div className="file-location-row">
                                            <span className="page-context-compact">
                                                📍 {(() => {
                                                    const pageInfo = formatPageContext(item.page_context);
                                                    return pageInfo.url ? (
                                                        <a 
                                                            href={pageInfo.url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="page-link"
                                                            title="Click to open page in new tab"
                                                        >
                                                            {pageInfo.text}
                                                        </a>
                                                    ) : (
                                                        <span>{pageInfo.text}</span>
                                                    );
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Right: Priority Score with Inline Breakdown */}
                                    <div className="priority-score-section">
                                        {/* Compact Priority Display with Inline Calculation */}
                                        <div className="priority-display-compact">
                                            <div className="priority-score-row">
                                                <div className="priority-score-large">{safePriority(item).toFixed(2)}</div>
                                                {item.processing_status === 'pending' && (
                                                    <div className="priority-adjust">
                                                        <button 
                                                            className="priority-btn-small increase"
                                                            onClick={() => handlePriorityAdjust(item.id, 0.01)}
                                                            title="Increase priority by 0.01"
                                                        >
                                                            +
                                                        </button>
                                                        <button 
                                                            className="priority-btn-small decrease"
                                                            onClick={() => handlePriorityAdjust(item.id, -0.01)}
                                                            title="Decrease priority by 0.01"
                                                        >
                                                            -
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="priority-calculation-inline">
                                                        {item.similarity_group_id ? (
                                                    // Grouped feedback: users + average reputation
                                                    <span className="calc-formula">
                                                        {item.group_user_count || 1} + {(Number(item.group_avg_credibility) || 0).toFixed(2)} = {safePriority(item).toFixed(2)}
                                                    </span>
                                                ) : (
                                                    // Individual feedback: 1 + user reputation  
                                                    <span className="calc-formula">
                                                        1 + {(Number(item.user_reputation) || 0).toFixed(2)} = {(1 + (Number(item.user_reputation) || 0)).toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                            {item.admin_priority_adjustment && item.admin_priority_adjustment !== 0 && (
                                                <div className="admin-adjustment-badge-compact">
                                                    Admin {item.admin_priority_adjustment > 0 ? '+' : ''}{item.admin_priority_adjustment.toFixed(2)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Main Content - More Compact */}
                                <div className="feedback-content-compact">
                                    {/* AI Summary - More Compact */}
                                    <div className="ai-summary-compact">
                                        <h4>🤖 AI Summary</h4>
                                        <p>{item.ai_summary || 'Processing...'}</p>
                                    </div>

                                    {/* Admin Edits Display for Processed Items */}
                                    {item.processing_status === 'processed' && (item.developer_notes || item.admin_tags || item.admin_comments) && (
                                        <div className="admin-edits-display">
                                            <h4>👤 Admin Edits</h4>
                                            {item.developer_notes && (
                                                <div className="admin-edit-item">
                                                    <strong>📝 Developer Notes:</strong>
                                                    <p>{item.developer_notes}</p>
                                                </div>
                                            )}
                                            {item.admin_tags && (
                                                <div className="admin-edit-item">
                                                    <strong>🏷️ Admin Tags:</strong>
                                                    <div className="admin-tags">
                                                        {(() => {
                                                            try {
                                                                const tags = typeof item.admin_tags === 'string' ? JSON.parse(item.admin_tags) : item.admin_tags;
                                                                return Array.isArray(tags) ? tags.map((tag, idx) => (
                                                                    <span key={idx} className="admin-tag">{tag}</span>
                                                                )) : <span className="admin-tag">{item.admin_tags}</span>;
                                                            } catch (e) {
                                                                return <span className="admin-tag">{item.admin_tags}</span>;
                                                            }
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                            {item.admin_comments && (
                                                <div className="admin-edit-item">
                                                    <strong>💬 Admin Comments:</strong>
                                                    <p>{item.admin_comments}</p>
                                                </div>
                                            )}
                                            {item.admin_edited_at && (
                                                <div className="admin-edit-metadata">
                                                    <small>✏️ Edited by {item.admin_edited_by || 'admin'} on {new Date(item.admin_edited_at).toLocaleString()}</small>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* CBA Prompt Display for Processed Items */}
                                    {item.processing_status === 'processed' && item.cba_prompt && (
                                        <div className="cba-prompt-display">
                                            <h4>🚀 Generated CBA LLM Prompt</h4>
                                            <div className="cba-prompt-content">
                                                <p>{item.cba_prompt}</p>
                                            </div>
                                            <div className="cba-prompt-note">
                                                <small>💡 This prompt will be sent to the CBA LLM when approved</small>
                                            </div>
                                        </div>
                                    )}

                                    {/* Code Location - Simplified */}
                                    {item.page_context && (
                                        <div className="code-location-compact">
                                            <h5>📂 Code Context</h5>
                                            <div className="location-details-compact">
                                                {(() => {
                                                    try {
                                                        const pageContext = typeof item.page_context === 'string' 
                                                            ? JSON.parse(item.page_context) 
                                                            : item.page_context;
                                                        return (
                                                            <div className="location-grid">
                                                                <span className="location-item">📄 {pageContext.url}</span>
                                                                <span className="location-item">🎯 {pageContext.section}</span>
                                                                <span className="location-item">⚙️ {pageContext.widget}</span>
                                                            </div>
                                                        );
                                                    } catch (e) {
                                                        return <span className="location-item">📄 Page context available</span>;
                                                    }
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Section - Based on Status */}
                                    <div className="feedback-actions-compact">
                                        {item.processing_status === 'pending' ? (
                                            // Pending items show human review interface
                                            <div className="pending-review-section-compact">
                                                {/* Category Selection - More Compact */}
                                                <div className="category-selector-compact">
                                                    <label className="section-label">
                                                        📂 Adjust Categories (AI Selected: {item.category}, Selected: {getSelectedCategories(item).length}):
                                                    </label>
                                                    <div className="category-buttons-compact">
                                                        {['property_management', 'ai_assistant', 'legal_platform', 'user_management', 'ui_design', 'performance', 'mobile_experience', 'bug_reports', 'feature_requests'].map(cat => {
                                                            const isSelected = isCategorySelected(item.id, cat);
                                                            return (
                                                                <button
                                                                    key={cat}
                                                                    className={`category-btn-compact ${isSelected ? 'selected' : ''}`}
                                                                    onClick={() => handleCategoryChange(item.id, cat)}
                                                                >
                                                                    {getCategoryDisplayName(cat)}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Code Location Editor - More Compact */}
                                                <div className="code-location-editor-compact">
                                                    <label className="section-label">📍 Adjust Code Location:</label>
                                                    <div className="location-inputs-compact">
                                                        <input
                                                            type="text"
                                                            placeholder="File path"
                                                            value={pendingEdits[item.id]?.file_path || item.precise_file_path || ''}
                                                            onChange={(e) => handleLocationChange(item.id, 'file_path', e.target.value)}
                                                            className="location-input-compact"
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Component"
                                                            value={pendingEdits[item.id]?.component || item.component_name || ''}
                                                            onChange={(e) => handleLocationChange(item.id, 'component', e.target.value)}
                                                            className="location-input-compact"
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Line range"
                                                            value={pendingEdits[item.id]?.line_range || item.line_range || ''}
                                                            onChange={(e) => handleLocationChange(item.id, 'line_range', e.target.value)}
                                                            className="location-input-compact"
                                                        />
                                                    </div>
                                                </div>

                                                {/* User Reputation Notice - More Compact */}
                                                <div className={`reputation-notice-compact ${item.user_reputation < 1.0 ? 'requires-vetting' : 'auto-reviewable'}`}>
                                                    {item.user_reputation < 1.0 ? (
                                                        <span>⚠️ User reputation {item.user_reputation?.toFixed(2)} - Requires manual vetting</span>
                                                    ) : (
                                                        <span>✅ User reputation {item.user_reputation?.toFixed(2)} - AI reviewable</span>
                                                    )}
                                                </div>

                                                {/* Developer Notes - For AI Prompt Enhancement */}
                                                <div className="developer-notes-section">
                                                    <label className="section-label">👨‍💻 Developer Notes (for AI prompt):</label>
                                                    <textarea
                                                        placeholder="Add specific instructions for the AI prompt (e.g., 'Focus on mobile responsiveness', 'Ensure backward compatibility', 'Use existing component patterns')..."
                                                        value={pendingEdits[item.id]?.developer_notes || ''}
                                                        onChange={(e) => handleLocationChange(item.id, 'developer_notes', e.target.value)}
                                                        className="developer-notes-input"
                                                        rows="3"
                                                    />
                                                </div>

                                                {/* Process/Reject Buttons - More Prominent */}
                                                <div className="pending-actions-compact">
                                                    <button 
                                                        className="action-btn-compact process"
                                                        onClick={() => handlePendingAction(item.id)}
                                                    >
                                                        ✅ Process → Processed
                                                    </button>
                                                    <button 
                                                        className="action-btn-compact reject"
                                                        onClick={() => openRejectModal(item)}
                                                    >
                                                        ❌ Reject
                                                    </button>
                                                </div>
                                            </div>
                                        ) : item.processing_status === 'processed' ? (
                                            // Processed items can be approved, rejected, or undone
                                            <div className="processed-actions">
                                                <div className="main-actions">
                                                    {index === 0 ? (
                                                        <button 
                                                            className="action-btn approve-top"
                                                            onClick={() => handleAction(item.id, 'approve')}
                                                        >
                                                            ✅ Send Top Prompt to CBA LLM
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="action-btn approve"
                                                            onClick={() => handleAction(item.id, 'approve')}
                                                        >
                                                            ✅ Send Prompt to CBA LLM
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="secondary-actions">
                                                    <button 
                                                        className="action-btn reject secondary"
                                                        onClick={() => openRejectModal(item)}
                                                        title="Reject with a reason — user receives email"
                                                    >
                                                        ❌ Reject
                                                    </button>
                                                    <button 
                                                        className="action-btn undo secondary"
                                                        onClick={() => handleAction(item.id, 'undo')}
                                                        title="Restore this feedback to pending status for re-review"
                                                    >
                                                        ↶ Undo
                                                    </button>
                                                </div>
                                            </div>
                                        ) : item.processing_status === 'approved' ? (
                                            // Approved items show completion status
                                            <div className="approved-actions">
                                                <div className="status-display approved">
                                                    ✅ Sent to CBA LLM
                                                </div>
                                                <div className="secondary-actions" style={{ marginTop: 10 }}>
                                                    <button
                                                        className="action-btn reject secondary"
                                                        onClick={() => openRejectModal(item)}
                                                        title="Reject with a reason — user receives email (balance unchanged)"
                                                    >
                                                        ❌ Reject
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Rejected items show undo and delete options
                                            <div className="rejected-actions">
                                                <div className="status-display rejected">
                                                    ❌ Rejected
                                                </div>
                                                {item.rejection_reason ? (
                                                    <div className="rejection-reason-admin">
                                                        <span className="rejection-reason-label">Reason sent to user</span>
                                                        <p className="rejection-reason-text">{item.rejection_reason}</p>
                                                    </div>
                                                ) : null}
                                                <div className="rejected-action-buttons">
                                                    <button 
                                                        className="action-btn undo"
                                                        onClick={() => handleAction(item.id, 'undo')}
                                                        title="Restore this feedback to pending status"
                                                    >
                                                        ↶ Undo
                                                    </button>
                                                    <button 
                                                        className="action-btn delete-forever"
                                                        onClick={() => handleAction(item.id, 'delete_forever')}
                                                        title="Hide from dashboard permanently (kept in database for audit)"
                                                    >
                                                        🗑️ Delete Forever
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Individual Feedback Entries */}
                                <details 
                                    className="original-feedback"
                                    onToggle={(e) => {
                                        if (e.target.open) {
                                            handleExpandGroup(item);
                                        }
                                    }}
                                >
                                    <summary>
                                        📄 View Original Feedback 
                                        {item.similarity_group_id && (
                                            <span className="group-count">
                                                ({item.group_user_count || 1} users)
                                            </span>
                                        )}
                                    </summary>
                                    
                                    <div className="individual-feedback-list">
                                        {item.similarity_group_id && individualFeedback[item.similarity_group_id] ? (
                                            // Show all individual feedback entries in the group
                                            individualFeedback[item.similarity_group_id].map((entry) => (
                                                <div key={entry.id} className="individual-entry">
                                                    <div className="entry-header">
                                                        <div className="user-info">
                                                            <strong>👤 {entry.user_email}</strong>
                                                            <span className="timestamp">
                                                                🕒 {new Date(entry.timestamp).toLocaleString()}
                                                            </span>
                                                            <span className="reputation-detail">
                                                                Rep: {entry.user_reputation?.toFixed(2) || '0.00'}
                                                            </span>
                                                        </div>
                                                        <button 
                                                            className="action-btn reply small"
                                                            onClick={() => handleReply(entry)}
                                                            title="Send email reply to this user"
                                                        >
                                                            📧 Reply
                                                        </button>
                                                    </div>
                                                    <div className="entry-text">
                                                        {entry.original_text}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            // Show single feedback if not grouped or not loaded yet
                                            <div className="individual-entry">
                                                <div className="entry-header">
                                                    <div className="user-info">
                                                        <strong>👤 {item.user_email}</strong>
                                                        <span className="timestamp">
                                                            🕒 {new Date(item.timestamp).toLocaleString()}
                                                        </span>
                                                        <span className="reputation-detail">
                                                            Rep: {item.user_reputation?.toFixed(2) || '0.00'}
                                                        </span>
                                                    </div>
                                                    <button 
                                                        className="action-btn reply small"
                                                        onClick={() => handleReply(item)}
                                                        title="Send email reply to this user"
                                                    >
                                                        📧 Reply
                                                    </button>
                                                </div>
                                                <div className="entry-text">
                                                    {item.original_text}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Loading state for grouped feedback */}
                                        {item.similarity_group_id && !individualFeedback[item.similarity_group_id] && expandedGroups[item.id] && (
                                            <div className="loading-individual">
                                                🔄 Loading individual feedback entries...
                                            </div>
                                        )}
                                    </div>
                                </details>
                            </div>
                        );
                    })
                )}

                                        {/* Show all items - No pagination needed */}
                    <div className="all-items-info">
                        <div className="total-items-display">
                            <strong>📋 Showing all {pagination.totalItems || feedback.length} feedback items</strong>
                        </div>
                    </div>
                </div>
            
            {/* Reject feedback — requires reason; emails submitter */}
            {rejectModalFeedback && (
                <div className="modal-overlay" onClick={() => setRejectModalFeedback(null)}>
                    <div className="reply-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>❌ Reject feedback</h3>
                            <button type="button" className="close-btn" onClick={() => setRejectModalFeedback(null)}>
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="feedback-context">
                                <p className="reject-modal-hint">
                                    The submitter will receive a branded email with <strong>this explanation</strong> and their
                                    <strong> current Opti token balance</strong>. Minimum 8 characters.
                                </p>
                                <p>
                                    <strong>From:</strong> {rejectModalFeedback.user_email}
                                </p>
                                <p>
                                    <strong>Preview:</strong> &quot;{(rejectModalFeedback.original_text || '').slice(0, 200)}
                                    {(rejectModalFeedback.original_text || '').length > 200 ? '…' : ''}&quot;
                                </p>
                            </div>
                            <div className="form-group">
                                <label htmlFor="reject-reason">Rejection reason</label>
                                <textarea
                                    id="reject-reason"
                                    rows={6}
                                    value={rejectReasonDraft}
                                    onChange={(e) => setRejectReasonDraft(e.target.value)}
                                    placeholder="e.g. Duplicate of an existing report, out of scope for the current release, or needs more detail to reproduce…"
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setRejectModalFeedback(null)}>
                                    Cancel
                                </button>
                                <button type="button" className="send-btn reject-confirm-btn" onClick={submitReject}>
                                    Reject &amp; notify user
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reply Modal */}
            {showReplyModal && replyTarget && (
                <div className="modal-overlay" onClick={() => setShowReplyModal(false)}>
                    <div className="reply-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>📧 Reply to Feedback</h3>
                            <button 
                                className="close-btn"
                                onClick={() => setShowReplyModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="feedback-context">
                                <h4>Original Feedback:</h4>
                                <p>"{replyTarget.original_text}"</p>
                                <div className="user-info">
                                    <strong>From:</strong> {replyTarget.user_email}
                                </div>
                            </div>
                            
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                sendReply(formData.get('subject'), formData.get('message'));
                            }}>
                                <div className="form-group">
                                    <label htmlFor="subject">Subject:</label>
                                    <input 
                                        type="text" 
                                        id="subject" 
                                        name="subject"
                                        defaultValue={`Re: ${replyTarget.ai_summary || 'Your feedback'}`}
                                        required 
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="message">Message:</label>
                                    <textarea 
                                        id="message" 
                                        name="message"
                                        rows="6"
                                        placeholder="Thank you for your feedback..."
                                        required
                                    ></textarea>
                                </div>
                                
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setShowReplyModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="send-btn">
                                        📧 Send Reply
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Process Prompt Modal */}
            {showPromptModal && promptResult && (
                <div className="prompt-modal">
                    <div className="prompt-modal-content">
                        <div className="prompt-header">
                            <h3 className="prompt-title">⚡ CBA LLM Development Prompt</h3>
                            <button 
                                className="prompt-close"
                                onClick={() => setShowPromptModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        {/* Feedback Summary Stats */}
                        <div className="prompt-stats">
                            <div className="prompt-stat">
                                <div className="prompt-stat-value">{promptResult.user_count || 1}</div>
                                <div className="prompt-stat-label">Users</div>
                            </div>
                            <div className="prompt-stat">
                                <div className="prompt-stat-value">{promptResult.priority_score?.toFixed(2) || '0.00'}</div>
                                <div className="prompt-stat-label">Priority</div>
                            </div>
                            <div className="prompt-stat">
                                <div className="prompt-stat-value">{promptResult.category}</div>
                                <div className="prompt-stat-label">Category</div>
                            </div>
                            <div className="prompt-stat">
                                <div className="prompt-stat-value">{promptResult.avg_credibility?.toFixed(2) || '0.00'}</div>
                                <div className="prompt-stat-label">Credibility</div>
                            </div>
                        </div>

                        {/* Generated LLM Prompt */}
                        <div className="prompt-section">
                            <h4>🤖 Generated Development Prompt</h4>
                            <div className="prompt-content">
                                {promptResult.llm_prompt || 'Generating prompt...'}
                            </div>
                        </div>

                        {/* Feedback Summary */}
                        <div className="prompt-section">
                            <h4>📋 Feedback Summary</h4>
                            <div className="prompt-content">
                                {promptResult.feedback_summary || promptResult.ai_summary}
                            </div>
                        </div>

                        {/* Implementation Context */}
                        <div className="prompt-section">
                            <h4>📍 Implementation Context</h4>
                            <div className="prompt-content">
                                Page: {promptResult.page_url || 'Unknown'}
                                Section: {promptResult.page_section || 'Unknown'}
                                
                                Suggested Location:
                                File: {promptResult.precise_file_path || 'To be determined'}
                                Component: {promptResult.component_name || 'Unknown'}
                                Lines: {promptResult.line_range || '1-50'}
                                
                                Search Terms: {promptResult.ide_search_terms ? JSON.stringify(promptResult.ide_search_terms) : 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default FeedbackManager; 